/* Panel shell — drawer / desktop panel structure, header, body, gallery sections */

import {A11Y_FOCUS_VISIBLE} from '@amblique/king-living/app/theme/foundations/a11y-focus-visible';

/** Panel header title: responsive grey; used by `headerTitle` only (not PDP trigger). */
const CHOOSE_LAYOUT_HEADING_COLOR = {
  base: 'brandColors.greyscale',
  lg: 'brandColors.greyscale90',
};

export const panelShellParts = [
  'overlay',
  'content',
  'header',
  'headerTitle',
  'galleryViewHeader',
  'galleryViewHeaderTitle',
  'galleryViewHeaderTitleProductDetail',
  'galleryViewHeaderTitleCoverDetail',
  'galleryViewBackButton',
  'mobileStepBackButton',
  'closeButton',
  'panelModeDropdown',
  'panelModePickerRoot',
  'panelModePickerTrigger',
  'panelModePickerChevron',
  'panelModePickerCheckIcon',
  'panelModePickerMenuList',
  'panelModePickerMenuItem',
  'panelModePickerMenuItemFirst',
  'panelModePickerMenuItemLast',
  'panelModePickerMenuItemSelected',
  'panelModePickerOptionLabel',
  'panelModePickerOptionLabelSelected',
  'panelModePickerOptionsSurface',
  'panelModePickerModalOverlay',
  'panelModePickerModalContent',
  'panelModePickerModalBody',
  'panelModePickerList',
  'panelModePickerListButton',
  'panelModePickerListButtonFirst',
  'panelModePickerListButtonLast',
  'panelModePickerListButtonSelected',
  'panelModePickerConfirmOverlay',
  'panelModePickerConfirmContent',
  'panelModePickerConfirmCloseButton',
  'panelModePickerConfirmHeader',
  'panelModePickerConfirmBody',
  'panelModePickerConfirmFooter',
  'panelModePickerConfirmButton',
  'panelModePickerConfirmButtonPrimary',
  'panelModePickerConfirmButtonOutline',
  'body',
  'emptyState',
  'mobileImageHeader',
  'mobileImageHeaderImage',
  'mobileExpandButton',
  'desktopPanel',
  'desktopPanelHeader',
  'desktopPanelBackButton',
  'panelHeaderTitleRow',
  'panelHeaderTitleLabel',
  'panelHeaderTitleLabelMultiSlot',
  'panelHeaderStepIndicator',
  'desktopPanelCloseButton',
  'galleryPanel',
  'galleryPanelHeader',
  'galleryPanelBackButton',
  'galleryPanelTitle',
  'configurationPanelRoot',
  'galleryOverlayContent'
];

export const panelShellBaseStyle = {
  overlay: {
    backdropFilter: 'blur(4px)',
    bg: 'blackAlpha.300',
  },
  content: {
    bg: 'white',
    display: 'flex',
    flexDirection: 'column',
    marginX: {base: 0},
    maxWidth: {base: '100%', lg: '480px'},
    width: {base: '100%'},
  },
  header: {
    alignItems: 'center',
    bg: 'white',
    display: 'flex',
    flexShrink: 0,
    maxH: {base: '60px', lg: 'unset'},
    minH: {base: '60px', lg: 'unset'},
    overflow: 'hidden',
    paddingLeft: {base: 6, lg: 8},
    paddingRight: {base: 6, lg: 8},
    paddingTop: {base: 3, lg: 1},
    paddingBottom: {base: 3, lg: 4},
  },
  headerTitle: {
    color: CHOOSE_LAYOUT_HEADING_COLOR,
    fontFamily: 'body',
    fontSize: {base: 'sm', lg: 'md'},
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: 1.375,
  },
  galleryViewHeader: {
    alignItems: 'center',
    bg: 'white',
    display: 'flex',
    flexShrink: 0,
    maxH: {base: '60px', lg: 'unset'},
    minH: {base: '60px', lg: 'unset'},
    overflow: 'hidden',
    paddingX: {base: 5, lg: 5},
    paddingY: {base: 3, lg: 5},
  },
  /** Gallery header — "View layout details" (Mobile/text.label.default) */
  galleryViewHeaderTitle: {
    color: 'greyscale.100',
    flex: 1,
    fontFamily: 'body',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '140%',
    minWidth: 0,
    sx: {
      fontVariantNumeric: 'lining-nums proportional-nums',
    },
  },
  /** Mobile: multi-type slot "See details" full-screen gallery header (`{label} details`) */
  galleryViewHeaderTitleProductDetail: {
    color: {base: 'grayAlpha.90', lg: 'gray.900'},
    flex: 1,
    fontFamily: 'body',
    fontSize: {base: '16px', lg: 'sm'},
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'base',
    minWidth: 0,
    sx: {
      fontVariantNumeric: 'lining-nums proportional-nums',
    },
  },
  /** Mobile gallery title when opening cover "See details" (Fabric / Leather details, etc.) */
  galleryViewHeaderTitleCoverDetail: {
    color: {base: 'grayAlpha.90', lg: 'gray.900'},
    flex: 1,
    fontFamily: 'body',
    fontSize: {base: '16px', lg: 'sm'},
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'base',
    minWidth: 0,
    sx: {
      fontVariantNumeric: 'lining-nums proportional-nums',
    },
  },
  galleryViewBackButton: {
    alignItems: 'center',
    color: 'gray.600',
    display: 'flex',
    flexShrink: 0,
    h: 5,
    justifyContent: 'center',
    minW: 5,
    p: 0,
    variant: 'unstyled',
    w: 5,
    sx: {
      '& svg': {
        height: 5,
        width: 5,
      },
    },
    _hover: {
      bg: 'gray.50',
    },
    _focusVisible: {
      boxShadow: '0 0 0 2px rgba(27, 26, 24, 0.35)',
      outline: 'none',
    },
  },
  /** Customize step header — back control (mobile), 20px hit area per design */
  mobileStepBackButton: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    h: '20px',
    justifyContent: 'center',
    maxH: '20px',
    minH: '20px',
    minW: '20px',
    p: 0,
    variant: 'ghost',
    w: '21px',
    sx: {
      '& svg': {
        display: 'block',
        height: '20px',
        width: '21px',
      },
    },
    _hover: {
      bg: 'transparent',
    },
    _focusVisible: {
      boxShadow: '0 0 0 2px rgba(27, 26, 24, 0.35)',
      outline: 'none',
    },
  },
  closeButton: {
    alignItems: 'center',
    bg: 'white',
    borderRadius: 'base',
    boxShadow: 'unset',
    color: 'gray.900',
    display: 'flex',
    flexShrink: 0,
    h: 5,
    justifyContent: 'center',
    minW: 5,
    p: 0,
    variant: 'unstyled',
    w: 5,
    zIndex: 2,
    sx: {
      '& svg': {
        height: 5,
        width: 5,
      },
    },
    _hover: {
      bg: 'gray.50',
    },
    ...A11Y_FOCUS_VISIBLE,
  },
  panelModeDropdown: {
    flex: 1,
    fontSize: {base: 'lg', lg: 'xl'},
    fontWeight: 'bold',
    maxWidth: '100%',
    minWidth: 0,
  },
  panelModePickerRoot: {
    flex: 1,
    maxWidth: '100%',
    minWidth: 0,
  },
  panelModePickerTrigger: {
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    color: {base: 'gray.900', lg: 'grayAlpha.90'},
    cursor: 'pointer',
    display: 'flex',
    fontSize: {base: '14px', lg: '16px'},
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 600,
    justifyContent: 'flex-start',
    lineHeight: 1.4,
    minWidth: 0,
    paddingX: 0,
    paddingY: 0,
    textAlign: 'left',
    width: '100%',
    ...A11Y_FOCUS_VISIBLE,
  },
  panelModePickerChevron: {
    color: 'brandColors.primaryDarkLinen',
    flexShrink: 0,
    height: '20px',
    width: '20px',
  },
  panelModePickerCheckIcon: {
    boxSize: '18px',
  },
  panelModePickerMenuList: {
    alignItems: 'flex-start',
    background: '#FFF',
    borderRadius: '4px',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 2px 6px 2px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'min(70vh, 480px)',
    maxW: '219px',
    minW: 0,
    overflowY: 'auto',
    paddingX: 0,
    paddingY: 0,
    outline: 'none',
    border: 'none',
  },
  panelModePickerMenuItem: {
    _active: {bg: '#FFF'},
    _focus: {bg: '#FAFAFA'},
    _focusVisible: {bg: '#FAFAFA', boxShadow: 'inset 0 0 0 2px #1B96FF'},
    _hover: {bg: '#FAFAFA'},
    alignItems: 'center',
    alignSelf: 'stretch',
    bg: '#FFF',
    borderColor: '#E8E8E8',
    borderRadius: 0,
    borderStyle: 'solid',
    borderWidth: '1px',
    cursor: 'pointer',
    display: 'flex',
    flex: '1 0 0',
    gap: 4,
    margin: 0,
    minH: '67px',
    padding: '16px',
    width: '100%',
  },
  panelModePickerMenuItemFirst: {
    borderTopWidth: 0,
    paddingTop: '24px',
  },
  panelModePickerMenuItemLast: {
    borderBottomWidth: 0,
    paddingBottom: '24px',
  },
  panelModePickerMenuItemSelected: {
    _active: {
      bg: 'var(--Linen-02---secondary-bg, #F1EDE4)',
      borderColor: '#E8E8E8',
    },
    _focus: {
      bg: 'var(--Linen-02---secondary-bg, #F1EDE4)',
      borderColor: '#E8E8E8',
    },
    _hover: {
      bg: 'var(--Linen-02---secondary-bg, #F1EDE4)',
      borderColor: '#E8E8E8',
    },
    bg: 'var(--Linen-02---secondary-bg, #F1EDE4)',
    borderColor: '#E8E8E8',
  },
  panelModePickerOptionLabel: {
    color: '#1C1C1C',
    flex: 1,
    fontSize: '16px',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 600,
    lineHeight: 1.4,
    minWidth: 0,
  },
  panelModePickerOptionLabelSelected: {
    color: '#1C1C1C',
    flex: 1,
    fontSize: '16px',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 600,
    lineHeight: 1.4,
    minWidth: 0,
  },
  panelModePickerOptionsSurface: {
    alignItems: 'flex-start',
    background: '#FFF',
    border: 'none',
    borderRadius: '8px',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 2px 6px 2px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    outline: 'none',
    overflow: 'hidden',
    width: 'min(315px, calc(100vw - 32px))',
  },
  panelModePickerModalOverlay: {
    bg: 'rgba(0, 0, 0, 0.20)',
  },
  panelModePickerModalContent: {
    bg: 'transparent',
    border: 'none',
    borderRadius: 0,
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    h: 'auto',
    margin: '16px',
    maxH: 'min(85vh, 640px)',
    maxW: 'calc(100vw - 32px)',
    minH: 0,
    overflow: 'visible',
    position: 'relative',
    sx: {
      '& .chakra-modal__header': {display: 'none'},
      '& .chakra-modal__close-btn': {display: 'none'},
    },
    width: 'auto',
  },
  panelModePickerModalBody: {
    alignItems: 'center',
    bg: 'transparent',
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    maxH: 'min(65vh, 520px)',
    overflowY: 'auto',
    pb: 0,
    pt: 0,
    px: 0,
    width: '100%',
  },
  panelModePickerList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    width: '100%',
  },
  panelModePickerListButtonFirst: {
    borderTopWidth: 0,
    paddingTop: '24px',
  },
  panelModePickerListButtonLast: {
    borderBottomWidth: 0,
    paddingBottom: '24px',
  },
  panelModePickerListButton: {
    _active: {bg: '#FFF'},
    _hover: {bg: '#FAFAFA'},
    alignItems: 'center',
    alignSelf: 'stretch',
    bg: '#FFF',
    borderColor: '#E8E8E8',
    borderRadius: 0,
    borderStyle: 'solid',
    borderWidth: '1px',
    cursor: 'pointer',
    display: 'flex',
    flex: '1 0 0',
    gap: 4,
    margin: 0,
    minH: '67px',
    padding: '16px',
    textAlign: 'left',
    width: '100%',
  },
  panelModePickerListButtonSelected: {
    _active: {
      bg: 'var(--Linen-02---secondary-bg, #F1EDE4)',
      borderColor: '#E8E8E8',
    },
    _hover: {
      bg: 'var(--Linen-02---secondary-bg, #F1EDE4)',
      borderColor: '#E8E8E8',
    },
    bg: 'var(--Linen-02---secondary-bg, #F1EDE4)',
    borderColor: '#E8E8E8',
  },
  // Confirm change (choose set / customize) — AlertDialog in ConfigurationPanelModePicker
  panelModePickerConfirmOverlay: {
    WebkitBackdropFilter: 'blur(16px)',
    alignItems: 'flex-start',
    backdropFilter: 'blur(16px)',
    bg: 'rgba(0, 0, 0, 0.20)',
    display: 'flex',
    justifyContent: 'center',
    px: {base: '20px', md: '24px'},
    pb: 8,
  },
  panelModePickerConfirmContent: {
    alignItems: 'flex-end',
    bg: 'brandColors.primaryBG',
    borderRadius: '4px',
    boxShadow: 'md',
    display: 'flex',
    flexDirection: 'column',
    gap: {'base': '24px', 'md': '32px'},
    marginBottom: 0,
    marginTop: '134px',
    maxW: {base: 'min(335px, calc(100vw - 40px))', md: 'min(619px, calc(100vw - 32px))'},
    mx: 'auto',
    padding: '76px 32px 80px 32px',
    position: 'relative',
    width: '100%',
  },
  panelModePickerConfirmCloseButton: {
    _active: {bg: 'transparent'},
    _hover: {bg: 'transparent'},
    h: '20px',
    maxH: '20px',
    maxW: '20px',
    minH: '20px',
    minW: '20px',
    p: 0,
    position: 'absolute',
    right: {base: '20px', md: '32px'},
    top: {base: '32px', md: '24px'},
    w: '20px',
    zIndex: 1,
    ...A11Y_FOCUS_VISIBLE,
    sx: {
      '&.chakra-button': {
        height: '20px',
        minWidth: '20px',
      },
    },
  },
  panelModePickerConfirmHeader: {
    alignSelf: 'stretch',
    color: 'brandColors.primaryText90',
    fontFamily: '"Aktiv Grotesk", system-ui, sans-serif',
    fontSize: {base: '24px', lg: '32px'},
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: {base: '150%', lg: '140%'},
    padding: 0,
    pb: 0,
    pt: 0,
  },
  panelModePickerConfirmBody: {
    alignSelf: 'stretch',
    color: 'brandColors.primaryText90',
    fontFamily: '"Aktiv Grotesk", system-ui, sans-serif',
    fontSize: {base: '14px', lg: '16px'},
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '140%',
    padding: 0,
    px: 0,
    py: 0,
  },
  panelModePickerConfirmFooter: {
    alignSelf: 'stretch',
    borderTopWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    justifyContent: 'stretch',
    padding: 0,
    pt: 0,
    width: '100%',
    marginTop: {'base': '8px', 'md': '0'},
  },
  panelModePickerConfirmButton: {
    alignItems: 'center',
    borderRadius: '4px',
    boxSizing: 'border-box',
    display: 'flex',
    flex: 'none',
    h: '54px',
    justifyContent: 'center',
    maxH: '54px',
    minH: '54px',
    minW: 0,
    width: '100%',
    fontWeight: 600,
  },
  /** "Stay and keep editing" — filled default; hover inverts to outline */
  panelModePickerConfirmButtonPrimary: {
    bg: 'brandColors.greyscale',
    borderColor: 'brandColors.greyscale',
    borderStyle: 'solid',
    borderWidth: '1px',
    color: 'white',
    cursor: 'pointer',
    fontWeight: 600,
    _active: {
      bg: 'brandColors.greyscale10',
      borderColor: 'brandColors.greyscale90',
      color: 'brandColors.greyscale',
    },
    _hover: {
      bg: 'transparent',
      borderColor: 'brandColors.greyscale90',
      color: 'brandColors.greyscale',
    },
    ...A11Y_FOCUS_VISIBLE,
  },
  /** "Change set" — outline per design; unstyled in JSX so Chakra outline variant does not override hover */
  panelModePickerConfirmButtonOutline: {
    bg: 'transparent',
    borderColor: 'brandColors.greyscale90',
    borderStyle: 'solid',
    borderWidth: '1px',
    color: 'brandColors.greyscale',
    cursor: 'pointer',
    fontWeight: 600,
    _active: {
      bg: 'brandColors.greyscale10',
      borderColor: 'brandColors.greyscale90',
      color: 'brandColors.greyscale',
    },
    _hover: {
      bg: 'brandColors.greyscale',
      borderColor: 'brandColors.greyscale',
      color: 'white',
    },
    ...A11Y_FOCUS_VISIBLE,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    paddingX: {base: 5, lg: 8},
    paddingTop: {base: 6, lg: 8},
    paddingBottom: {base: 6, lg: 0},
    sx: {
      '&.customize-step-cover-content-body': {
        paddingTop: {base: 5, lg: 8},
        '& > .cover-step-container > .chakra-text, & > .cover-step-container > .cover-step__tabs': {
          marginBottom: {base: 8, lg: 10},
        },
      },
      '&.customize-steps-content-body.configuration-selector-panel-content__body': {
        paddingX: {base: 8, lg: 8},
        paddingTop: {base: 6, lg: 8},
        paddingBottom: {base: 6, lg: 0},
      },
    },
  },
  emptyState: {
    paddingY: 8,
    textAlign: 'center',
  },
  mobileImageHeader: {
    flexShrink: 0,
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
    bg: 'gray.50',
  },
  mobileImageHeaderImage: {
    height: '100%',
    objectFit: 'contain',
    width: '100%',
  },
  mobileExpandButton: {
    bottom: 2.5,
    color: 'grayAlpha.90',
    position: 'absolute',
    right: 10.25,
    size: 'sm',
    variant: 'ghost',
    boxShadow: 'unset',
    zIndex: 1,
    sx: {
      '& svg': {
        height: 5,
        width: 5,
      },
    },
  },
  desktopPanel: {
    height: '100%',
    minHeight: '500px',
    overflow: 'hidden',
  },
  desktopPanelHeader: {
    alignItems: 'center',
    alignSelf: 'stretch',
    background: '#FFF',
    borderBottom: '1px solid',
    borderColor: 'grayAlpha.10',
    display: 'flex',
    flexShrink: 0,
    maxHeight: '70px',
    minHeight: 0,
    padding: '24px',
    width: '100%',
    sx: {
      '& button': {
        height: '20px',
        minWidth: '20px',
      },
      '&.configuration-panel-header--choose-layout .product-tile__name ': {
        paddingRight: {base: 'unset', lg: '16px'},
      },
      '&.configuration-panel-header--customize-root': {
        maxHeight: 'none',
        overflow: 'visible',
      },
    },
  },
  desktopPanelBackButton: {
    flexShrink: 0,
    h: '20px',
    minW: '20px',
    p: 0,
    variant: 'ghost',
    w: 'auto',
    ...A11Y_FOCUS_VISIBLE,
    sx: {
      '& svg': {
        display: 'block',
        height: '20px',
        width: '21px',
      },
    },
  },
  panelHeaderTitleRow: {
    alignItems: 'center',
    flex: 1,
    gap: '16px',
    minWidth: 0,
  },
  panelHeaderTitleLabel: {
    color: 'brandColors.greyscale90',
    fontFamily: 'body',
    fontSize: {base: '16px', lg: '18px'},
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 500,
    lineHeight: {base: '140%', lg: '120%'},
    minWidth: 0,
  },
  panelHeaderTitleLabelMultiSlot: {
    color: 'brandColors.greyscale90',
    fontFamily: 'body',
    fontSize: {base: '16px', lg: '18px'},
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 500,
    lineHeight: {base: '140%', lg: '120%'},
    minWidth: 0,
  },
  panelHeaderStepIndicator: {
    color: 'brandColors.greyscale70',
    fontFamily: 'body',
    fontSize: {base: '16px', lg: '18px'},
    fontStyle: 'normal',
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 400,
    flexShrink: 0,
    lineHeight: {base: '140%', lg: '120%'},
  },
  desktopPanelCloseButton: {
    color: 'brandColors.greyscale',
    h: 6,
    minW: 6,
    p: 0,
    variant: 'ghost',
    _hover: {
      bg: 'transparent',
      opacity: 0.7,
    },
    _active: {
      bg: 'transparent',
    },
    ...A11Y_FOCUS_VISIBLE,
    sx: {
      '& svg': {
        height: 5,
        width: 5,
      },
    },
  },
  galleryPanel: {
    bg: 'white',
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    left: 0,
    overflow: 'hidden',
    position: 'fixed',
    right: 0,
    top: 0,
    width: '100vw',
    zIndex: 1500,
  },
  galleryPanelHeader: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: 4,
    maxH: {base: '60px', lg: 'unset'},
    minH: {base: '60px', lg: 'unset'},
    overflow: 'hidden',
    paddingX: 5,
    paddingY: {base: 3, lg: 5},
  },
  galleryPanelBackButton: {
    color: 'grayAlpha.90',
    flexShrink: 0,
    height: 5,
    width: 5,
    minWidth: 5,
    variant: 'ghost',
    sx: {
      '& svg': {
        height: 5,
        width: 5,
      },
    },
  },
  galleryPanelTitle: {
    color: 'grayAlpha.90',
    flex: 1,
    fontSize: {base: '16px', lg: 'md'},
    fontWeight: {base: 600, lg: 'semibold'},
    minWidth: 0,
  },
  configurationPanelRoot: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    width: '100%',
    sx: {
      '&.configuration-panel__root--choose-layout .configuration-panel-header, &.configuration-panel__root--gallery-detail .configuration-panel-header': {
        padding: {base: '20px', lg: '24px'},
      },
      '&.configuration-panel__root--customize-root .configuration-panel-header, &.configuration-panel__root--customize-step .configuration-panel-header, &.configuration-panel__root--gallery-detail .configuration-panel-header--gallery-product-detail': {
        paddingX: {base: '24px', lg: '24px'},
        paddingY: {base: '20px', lg: '24px'},
      },
      '&.configuration-panel__root--choose-layout .subtotal-footer img': {
        display: 'none',
      },
      '& .configuration-panel-body-wrapper': {
        '&:not([class*="configuration-panel-body-wrapper--customize-step"])': {
          '& .subtotal-footer img': {
            display: 'none',
          },
        },
      }
    },
  },
  galleryOverlayContent: {
    flex: 1,
    overflow: 'auto',
    gap: {base: '24px', lg: 4},
    sx: {
      overscrollBehavior: 'contain',
      WebkitOverflowScrolling: 'touch',
      '& .chakra-stack': {
        gap: {base: '24px', lg: 4},
      },
    },
  },
};
